import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {
  PurchaseOrder,
  PurchaseOrderCreateRequest,
  PurchaseOrderStatus,
  PurchaseOrderUpdateRequest
} from "../../api/model/purchase-order";
import {Equipment} from "../../api/model/equipment";
import {PurchaseOrderService} from "../../api/purchase-order.service";
import {apiErrorToMessage, highlightText, removeDiacritics} from "../../common/util";
import {EMPTY, fromEvent} from "rxjs";
import {MatSnackBar} from "@angular/material/snack-bar";
import {ActivatedRoute, Router} from "@angular/router";
import {MatCheckboxChange} from "@angular/material/checkbox";
import {AuthService} from "../../api/auth.service";
import {ViewportScroller} from "@angular/common";
import {MoveDialogData, PurchaseOrderMoveDialogComponent} from "../move-dialog/move-dialog.component";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {
  PurchaseOrderEditDialogComponent,
  PurchaseOrderEditDialogData
} from "../edit-dialog/purchase-order-edit-dialog.component";
import {MatAutocompleteSelectedEvent} from "@angular/material/autocomplete";
import {trim} from "lodash";
import {ManufacturerService} from "../../api/manufacturer.service";
import {FormControl} from "@angular/forms";
import {Manufacturer} from "../../api/model/manufacturer";

@Component({
  selector: 'app-purchase-order-list',
  templateUrl: './list.component.html',
  styleUrl: './list.component.sass'
})
export class PurchaseOrderListComponent implements OnInit, AfterViewInit {
  purchaseOrders!: PurchaseOrder[];
  loading = true;
  groupBySku = false;
  showOnlyMine = false;
  activeFragment: string | null = null;
  selection: { [key: string]: Equipment[] } = {};

  manufacturerField = new FormControl();
  filteredManufacturers: Manufacturer[] = [];
  manufacturerList = this.manufacturerService.listSignal();
  @ViewChild("manufacturer") manufacturerFieldRef!: ElementRef;
  protected selectedManufacturer: string | undefined;
  protected readonly PurchaseOrderStatus = PurchaseOrderStatus;
  protected readonly alert = alert;
  protected readonly trim = trim;
  private statuses: PurchaseOrderStatus[] = [];
  private dialogRef: MatDialogRef<any> | undefined;

  constructor(
    private authService: AuthService,
    private purchaseOrderService: PurchaseOrderService,
    private manufacturerService: ManufacturerService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private scroller: ViewportScroller,
    private _changeDetectorRef: ChangeDetectorRef,
    private _snackBar: MatSnackBar,
  ) {
    this.groupBySku = localStorage.getItem(`purchaseOrder_groupBySku`) === "true";
    this.showOnlyMine = localStorage.getItem(`purchaseOrder_showOnlyMine`) === "true";
    const storedManufacturer = localStorage.getItem(`purchaseOrder_manufacturer`)
    if (storedManufacturer) {
      this.manufacturerField.setValue(storedManufacturer || "");
      this.selectedManufacturer = storedManufacturer;
    }
  }

  ngOnInit(): void {
    this.route.data.subscribe(data => {
      this.statuses = data["statuses"];
      console.log("PurchaseOrderListComponent loading statuses", this.statuses);
      this.refetch();
    });
    this.route.fragment.subscribe(fragment => {
      this.activeFragment = fragment;
    });
  }

  ngAfterViewInit(): void {
    const keyUpEvent = fromEvent(this.manufacturerFieldRef.nativeElement, 'keyup')
    keyUpEvent.subscribe(value => {
      const filterValue = removeDiacritics(this.manufacturerFieldRef.nativeElement.value);
      console.log("EquipmentAddEditDialogComponent manufacturer value changing", filterValue, value);
      this.filteredManufacturers = this.manufacturerList()?.filter(m => removeDiacritics(m.name).includes(filterValue)) || [];
    });
  }

  refetch(focusPO?: PurchaseOrder): void {
    this.loading = true;
    const responsible = this.showOnlyMine ? [this.authService.currentUser?.email || ""] : undefined;
    this.purchaseOrderService.list(this.statuses, responsible, this.groupBySku, undefined, this.selectedManufacturer).subscribe({
      next: orders => {
        console.log("PurchaseOrderListComponent orders loaded", orders);
        this.purchaseOrders = orders;
        if (focusPO) {
          this.focus(focusPO);
        }
        if (this.activeFragment) {
          // TODO impl... doesn't work for some bloody reason.
          // this.scroller.scrollToAnchor(this.activeFragment);
          // console.log("PurchaseOrderListComponent activeFragment", this.scroller.getScrollPosition(), this.activeFragment);
        }
        this._changeDetectorRef.markForCheck();
      },
      error: error => {
        const msg = apiErrorToMessage(error);
        this._snackBar.open(`Unfortunate error occurred. ${msg}`, "Okay :(");
        console.error("PurchaseOrderListComponent error", msg)
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  toDraft(p: PurchaseOrder) {
    const eq = this.selection[p.name];
    if (eq.length > 0) {
      this.create({
        name: p.name,
        notes: "",
        equipmentIds: eq.map(e => e.id),
        status: PurchaseOrderStatus.DRAFT,
      });
    }

  }

  sendOrder(p: PurchaseOrder) {
    this.update(p, {status: PurchaseOrderStatus.PENDING_INFO});
  }

  confirmOrder(p: PurchaseOrder) {
    this.edit(p, PurchaseOrderStatus.ORDERED);
  }

  equipmentChanged(p: PurchaseOrder, equipment: Equipment[]) {
    this.purchaseOrderService.get(p.id).subscribe({
      next: (po) => {
        console.log("PurchaseOrderListComponent fetch one", po);
        const poInPlace = this.purchaseOrders.find(p => p.id === po.id);
        if (poInPlace) {
          poInPlace.equipment = po.equipment;
        }
        if (poInPlace?.status != po.status) {
          this.refetch();
        }
        this._changeDetectorRef.markForCheck();
      },
      error: error => {
        console.log("PurchaseOrderListComponent updating failed", error)
        const msg = apiErrorToMessage(error);
        this._snackBar.open(`Could not update Purchase Order. ${msg}`, "okay... :(");
        return EMPTY
      }
    })
  }

  showOnlyMineChanged($event: MatCheckboxChange) {
    localStorage.setItem(`purchaseOrder_showOnlyMine`, String(this.showOnlyMine));
    this.refetch();
  }

  groupBySkuChanged($event: MatCheckboxChange) {
    localStorage.setItem(`purchaseOrder_groupBySku`, String(this.groupBySku));
    this.refetch();
  }

  clearManufacturer() {
    this.manufacturerAutocompleteSelect();
  }

  manufacturerAutocompleteSelect($event?: MatAutocompleteSelectedEvent) {
    const value = $event?.option.value || "";
    this.manufacturerField.setValue(value, {emitEvent: false, emitModelToViewChange: true});
    this.selectedManufacturer = this.manufacturerList()?.find(mfr => mfr.name === value)?.name;
    localStorage.setItem(`purchaseOrder_manufacturer`, value);
    this.refetch();
  }

  highlight(text: string, searchFor: string | undefined | null) {
    return highlightText(text, searchFor);
  }

  setActiveFragment(poName: string | null) {
    this.activeFragment = poName;
    this.router.navigate([], {fragment: poName || undefined});
  }

  selectionUpdated(p: PurchaseOrder, selected: Equipment[]) {
    this.selection[p.name] = selected;
  }

  remove(p: PurchaseOrder) {
    const eq = this.selection[p.name];
    if (eq.length > 0) {
      this.update(p, {equipmentIds: p.equipmentIds.filter(id => !eq.find(e => e.id == id))});
    }
  }

  move(p: PurchaseOrder) {
    const eq = this.selection[p.name];
    if (eq.length > 0) {
      const dialogData: MoveDialogData = {
        equipment: eq,
        from: p,
      }
      const dialogRef = this.dialog.open(PurchaseOrderMoveDialogComponent, {data: dialogData});
      this.dialogRef = dialogRef;
      dialogRef.afterClosed().subscribe(result => {
        this.dialogRef = undefined;
        if (result)
          this.refetch(result);
      });
    }
  }

  edit(p: PurchaseOrder, status?: PurchaseOrderStatus) {
    const dialogData: PurchaseOrderEditDialogData = {
      purchaseOrder: p,
      alsoSetStatus: status
    }
    const dialogRef = this.dialog.open(PurchaseOrderEditDialogComponent, {data: dialogData});
    this.dialogRef = dialogRef;
    dialogRef.afterClosed().subscribe(result => {
      this.dialogRef = undefined;
      if (result)
        this.refetch(result);
    });
  }

  exportXlsx(p: PurchaseOrder) {
    const eq = this.selection[p.name];
    if (eq.length > 0) {

    }
  }

  isButtonDisabled(p: PurchaseOrder) {
    const sel = this.selection[p.name];
    return sel === undefined || sel.length == 0;
  }

  delete(p: PurchaseOrder) {
    if (p.equipment.length > 0) {
      alert("Purchase Order has equipment. Remove it first, only then you can delete this PO.");
      return;
    }
    this.purchaseOrderService.delete(p.id).subscribe({
      next: (po) => {
        console.log("PurchaseOrderListComponent deleted", po);
        this.refetch();
      },
      error: error => {
        console.log("PurchaseOrderListComponent delete failed", error)
        const msg = apiErrorToMessage(error);
        this._snackBar.open(`Could not delete Purchase Order. ${msg}`, "okay... :(");
        return EMPTY;
      }
    });

  }

  private create(args: PurchaseOrderCreateRequest) {
    this.purchaseOrderService.create(args).subscribe({
      next: (po) => {
        console.log("PurchaseOrderListComponent created", po);
        this.refetch(po);
      },
      error: error => {
        console.log("PurchaseOrderListComponent create failed", error)
        const msg = apiErrorToMessage(error);
        this._snackBar.open(`Could not create Purchase Order. ${msg}`, "okay... :(");
        return EMPTY;
      }
    });
  }

  private update(p: PurchaseOrder, args: PurchaseOrderUpdateRequest) {
    this.purchaseOrderService.update(p.id, args).subscribe({
      next: (po) => {
        console.log("PurchaseOrderListComponent updated", args, po);
        this.refetch(po);
      },
      error: error => {
        console.log("PurchaseOrderListComponent updating failed", error)
        const msg = apiErrorToMessage(error);
        this._snackBar.open(`Could not update Purchase Order. ${msg}`, "okay... :(");
        return EMPTY;
      }
    });
  }

  private focus(focusPO: PurchaseOrder) {
    const path = purchaseOrderPath(focusPO);
    this.router.navigate(path.path, {fragment: path.fragment});
  }
}

export function purchaseOrderPath(purchaseOrder: PurchaseOrder): { path: string[], fragment: string } {
  const statusPath = purchaseOrder.status == PurchaseOrderStatus.GENERATED ? 'generated'
    : purchaseOrder.status == PurchaseOrderStatus.DRAFT ? 'drafts'
      : purchaseOrder.status == PurchaseOrderStatus.PENDING_INFO ? 'execution'
        : purchaseOrder.status == PurchaseOrderStatus.ORDERED ? 'execution'
          : purchaseOrder.status == PurchaseOrderStatus.COMPLETE ? 'complete' : 'unknown';

  return {path: ['purchase-order', statusPath], fragment: purchaseOrder.name};
}
